import { ReactNode } from "react";
import { useViewportVisibilityForAnimations } from "~/components/1_atoms/useViewportVisibility";
import { Button } from "~/components/2_molecules/button";

export interface ShowcaseItemProps {
  id?: string;
  /**
   * Main title for the showcase section
   */
  title: string | ReactNode;

  /**
   * Description text content
   */
  description: ReactNode;

  /**
   * Image configuration
   * Required when customContent is false or undefined
   */
  image?: {
    src: string;
    alt: string;
    alignment?: "top" | "bottom";
    mobileHide?: boolean;
  };

  /**
   * Position of the image relative to content
   * @default "auto" - alternates based on index
   */
  imagePosition?: "left" | "right" | "auto";

  /**
   * Index for auto alternating behavior (even = left, odd = right)
   * @default 0
   */
  index?: number;

  /**
   * Background decoration style
   * @default "rectangle"
   */
  backgroundStyle?: "rectangle" | "pattern" | "none";

  /**
   * Alignment of rectangle background decoration
   * @default "middle"
   */
  rectangleAlignment?: "top" | "middle";

  /**
   * Custom decoration pattern element
   */
  decorPattern?: ReactNode;

  /**
   * Call-to-action button configuration
   */
  cta?: {
    text: string;
    onClick: () => void;
    variant?: "default" | "carousel";
  };

  /**
   * Enable custom content rendering instead of image
   * When true, renders children prop content in place of image container
   * @default false
   */
  customContent?: boolean;

  /**
   * Custom content to render when customContent is true
   * Replaces the image container with this content
   */
  children?: ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Additional CSS classes for text column
   */
  textColumnClasses?: string;
}

export function ShowcaseItem({
  id,
  title,
  description,
  image,
  imagePosition = "auto",
  index = 0,
  backgroundStyle = "rectangle",
  rectangleAlignment = "middle",
  decorPattern,
  cta,
  customContent = false,
  children,
  className = "",
  textColumnClasses = "",
}: ShowcaseItemProps) {
  const { ref, isVisible } = useViewportVisibilityForAnimations();

  // Determine actual image position
  const actualImagePosition =
    imagePosition === "auto" ? (index % 2 === 0 ? "left" : "right") : imagePosition;

  // Calculate timing for sequential animations
  const imageDelay = 0;
  const contentDelay = 200;
  const titleDelay = contentDelay + 100;
  const descriptionDelay = titleDelay + 150;
  const ctaDelay = descriptionDelay + 150;

  // Generate CSS classes
  const sectionClasses = [
    "tg-showcase-item",
    `tg-showcase-item--${actualImagePosition}`,
    `tg-showcase-item--bg-${backgroundStyle}`,
    backgroundStyle === "rectangle" && `tg-showcase-item--rect-${rectangleAlignment}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article id={id} ref={ref} className="tg-section__wrapper">
      <div className={`tg-section__grid py-16 lg:py-12 ${sectionClasses}`}>
        {/* Image/Custom Content Container */}
        {customContent ? (
          /* Custom Content Container */
          <div className="tg-showcase-item__custom-container">
            <div
              className={`tg-showcase-item__custom-wrapper transition-all duration-700 ease-in-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
              }`}
              style={{
                transitionDelay: `${imageDelay}ms`,
              }}
            >
              {children}
            </div>
          </div>
        ) : (
          /* Image Container */
          <div
            className={`tg-showcase-item__image-container ${image?.mobileHide ? "hidden xl:block" : ""}`}
          >
            <div
              className={`tg-showcase-item__image-wrapper transition-all duration-700 ease-in-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
              }`}
              style={{
                transitionDelay: `${imageDelay}ms`,
              }}
            >
              {image && (
                <img
                  src={image.src}
                  alt={image.alt}
                  className="tg-showcase-item__image"
                  style={{ objectPosition: `${image.alignment}` }}
                  loading="lazy"
                />
              )}
            </div>

            {/* Background Rectangle/Pattern */}
            {backgroundStyle === "rectangle" && (
              <div
                className={`tg-showcase-item__background-rect transition-all duration-700 ease-in-out ${
                  isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"
                }`}
                style={{
                  transitionDelay: `${imageDelay + 100}ms`,
                }}
              />
            )}
            {backgroundStyle === "pattern" && decorPattern ? (
              <div
                className={`tg-showcase-item__background-pattern transition-all duration-700 ease-in-out ${
                  isVisible ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  transitionDelay: `${imageDelay + 100}ms`,
                }}
              >
                {decorPattern}
              </div>
            ) : null}
          </div>
        )}

        {/* Content Container */}
        <div className="tg-showcase-item__content">
          <div
            className={`tg-showcase-item__content-wrapper ${textColumnClasses} transition-all duration-700 ease-in-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            }`}
            style={{
              transitionDelay: `${contentDelay}ms`,
            }}
          >
            <h2
              className={`tg-showcase-item__title tg-h4 transition-all duration-700 ease-in-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
              }`}
              style={{
                transitionDelay: `${titleDelay}ms`,
              }}
            >
              {title}
            </h2>
            <div
              className={`tg-showcase-item__description tg-body transition-all duration-700 ease-in-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
              }`}
              style={{
                transitionDelay: `${descriptionDelay}ms`,
              }}
            >
              {description}
            </div>

            {/* CTA Button */}
            {cta && (
              <div
                className={`transition-all duration-700 ease-in-out ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
                style={{
                  transitionDelay: `${ctaDelay}ms`,
                }}
              >
                <Button
                  variant={cta.variant || "carousel"}
                  onClick={cta.onClick}
                  className="tg-showcase-item__cta"
                  size="lg"
                  pulsing={true}
                >
                  {cta.text}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
